import React, { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { syncTimeService } from '../../services/syncTimeService';
import { firestoreWrapper } from '../../services/firestoreWrapper';
import { useIsAdmin } from '../../hooks/useIsAdmin';

export const LastSyncIndicator: React.FC = () => {
  const { isAdmin, loading: isAdminLoading } = useIsAdmin();
  const [lastSyncTime, setLastSyncTime] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Only fetch data if user is admin or we're still checking admin status
    if (!isAdmin && !isAdminLoading) {
      return;
    }
    
    const fetchLastSyncTime = async () => {
      try {
        // First try to get from localStorage
        const cachedTime = syncTimeService.getLastKnownSync();
        if (cachedTime > 0) {
          setLastSyncTime(cachedTime);
          setIsLoading(false);
        }
        
        // Then try to get from Firestore (but only if we haven't checked recently)
        const now = Date.now();
        const lastCheckTime = syncTimeService.getLastSyncCheckTime();
        
        // Only check Firestore once per 15 minutes max
        if (now - lastCheckTime > 15 * 60 * 1000) {
          syncTimeService.storeLastSyncCheckTime(now);
          
          const syncDoc = await firestoreWrapper.getDoc(
            firestoreWrapper.doc(
              firestoreWrapper.collection('goodThingsSync'),
              'latest'
            )
          );
          
          if (syncDoc.exists()) {
            const lastSync = syncDoc.data().lastSync;
            if (lastSync) {
              const lastSyncTimestamp = lastSync.toMillis ? lastSync.toMillis() : new Date(lastSync).getTime();
              syncTimeService.storeLastKnownSync(lastSyncTimestamp);
              setLastSyncTime(lastSyncTimestamp);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching last sync time:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchLastSyncTime();
    
    // Set up a periodic check every 15 minutes
    const intervalId = setInterval(() => {
      fetchLastSyncTime();
    }, 15 * 60 * 1000);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [isAdmin, isAdminLoading]);
  
  // Don't render anything if user is not an admin (and we've confirmed admin status)
  if (!isAdmin && !isAdminLoading) {
    return null;
  }
  
  if (isLoading) {
    return <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Last Sync: Loading...</p>;
  }
  
  if (!lastSyncTime) {
    return <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Last Sync: Unknown</p>;
  }
  
  // Calculate how fresh the sync is
  const now = Date.now();
  const hoursSinceSync = (now - lastSyncTime) / (1000 * 60 * 60);
  
  // Determine color based on freshness
  let textColorClass = 'text-green-500 dark:text-green-400'; // Fresh (< 1 hour)
  if (hoursSinceSync > 3) {
    textColorClass = 'text-amber-500 dark:text-amber-400'; // Stale (> 3 hours)
  } else if (hoursSinceSync > 1) {
    textColorClass = 'text-yellow-500 dark:text-yellow-400'; // Aging (1-3 hours)
  }
  
  return (
    <p className={`text-xs mt-1 flex items-center ${textColorClass}`}>
      <Clock className="h-3 w-3 mr-1" />
      Last Sync: {syncTimeService.formatLastSyncTime(lastSyncTime)}
    </p>
  );
};

export default LastSyncIndicator;
